/**
 * Visit header Billing chip (PRD.163 Section 6).
 * Compact status + amount summary that opens the VisitBillingPanel on click.
 */

import { useMemo } from 'react';
import { NavIcon } from '../../../shell/shared';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import { qInvoicesByEncounter, type InvoiceRow } from '@helios/data-access';

// ─── Helpers ───

function fmtCurrency(cents: number | null): string {
  if (cents == null) return '—';
  return `₱${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

interface Summary {
  label: string;
  tone: 'neutral' | 'draft' | 'balance' | 'paid';
}

function summarize(invoices: InvoiceRow[]): Summary {
  if (invoices.length === 0) return { label: 'New', tone: 'neutral' };

  let totalCharged = 0;
  let totalPaid = 0;
  let hasDraft = false;
  for (const inv of invoices) {
    totalCharged += inv.grand_total ?? 0;
    if (inv.status === 'paid') totalPaid += inv.grand_total ?? 0;
    if (inv.status === 'draft') hasDraft = true;
  }
  const balance = totalCharged - totalPaid;

  if (balance <= 0 && totalCharged > 0) return { label: 'Paid', tone: 'paid' };
  if (hasDraft && totalCharged === 0) return { label: 'Draft', tone: 'draft' };
  return { label: `Balance ${fmtCurrency(balance)}`, tone: 'balance' };
}

const TONE_STYLES: Record<Summary['tone'], { bg: string; color: string; border: string }> = {
  neutral: {
    bg: 'var(--bg-surface)',
    color: 'var(--text-muted)',
    border: 'var(--border)',
  },
  draft: {
    bg: 'var(--color-warning-bg)',
    color: 'var(--color-warning-text)',
    border: 'var(--color-warning-border)',
  },
  balance: {
    bg: 'var(--color-warning-bg)',
    color: 'var(--color-warning-text)',
    border: 'var(--color-warning-border)',
  },
  paid: {
    bg: 'var(--color-success-bg)',
    color: 'var(--color-success-text)',
    border: 'var(--color-success-border, var(--border))',
  },
};

// ─── Props ───

interface Props {
  patientId: string;
  encounterId: string;
  onClick: () => void;
}

// ─── Component ───

export function VisitBillingChip({ patientId, encounterId, onClick }: Props) {
  const { data: invoiceRows = [] } = usePowerSyncQuery<InvoiceRow>(
    qInvoicesByEncounter(patientId, encounterId),
  );

  const summary = useMemo(() => summarize(invoiceRows), [invoiceRows]);
  const tone = TONE_STYLES[summary.tone];

  return (
    <button
      onClick={onClick}
      title="Open Billing"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        height: 32,
        padding: '0 10px',
        borderRadius: 'var(--radius-full)',
        border: `1px solid ${tone.border}`,
        background: tone.bg,
        color: tone.color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <NavIcon name="credit-card" />
      <span style={{ color: 'var(--text)', fontWeight: 600 }}>Billing</span>
      <span style={{ opacity: 0.55 }}>·</span>
      <span>{summary.label}</span>
    </button>
  );
}
